"use client";

import { useBusinessVariables } from "@grupa-pracuj/business-variables";
import {
  CookieAgreements,
  configureTcfManager,
  getConfig as getCookiesConfig,
  type TrackerData,
} from "@grupa-pracuj/cookies-library-sip";
import {
  Footer,
  getConfig as getFooterConfig,
} from "@grupa-pracuj/footer-library-sip";
import { useGatewayContext } from "@grupa-pracuj/gateway-library-sip";
import { Header } from "@grupa-pracuj/header-library-sip";
import { useSearchParams } from "next/navigation";
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useTracker } from "@/tracker";
import styles from "./HomeContent.module.css";

type User = ComponentProps<typeof Header>["user"];

configureTcfManager({
  cmpId: Number(process.env.NEXT_PUBLIC_TCF_CMP_ID),
  cmpVersion: Number(process.env.NEXT_PUBLIC_TCF_CMP_VERSION),
  cookieDomain: process.env.COOKIE_DOMAIN ?? "",
  vendorListUrl: process.env.NEXT_PUBLIC_GVL_VENDOR_LIST_URL ?? "",
  purposesUrl: process.env.NEXT_PUBLIC_GVL_PURPOSES_URL ?? "",
});

const HomeContent = ({
  children,
  user,
}: {
  children: ReactNode;
  user?: User;
}) => {
  const http = useGatewayContext();
  const searchParams = useSearchParams();
  const businessVariables = useBusinessVariables();
  const [forceOpenCookieAgreement, setForceOpenCookieAgreement] =
    useState(false);

  const { analyticsInstance, sendBaseAnalytics, sendCookieAgreementsModal } =
    useTracker();

  const year = searchParams.get("year") ?? "";
  const month = searchParams.get("month") ?? "";
  const pageNumber = searchParams.get("pageNumber") ?? "";
  const routeChangeKey = `${year}-${month}-${pageNumber}`;

  useEffect(() => {
    const trackBaseAnalytics = async (_routeKey: string) => {
      await sendBaseAnalytics();
    };

    trackBaseAnalytics(routeChangeKey).catch(() => {});
  }, [sendBaseAnalytics, routeChangeKey]);

  const handleCookieTrackerEvent = useCallback(
    async (data: TrackerData) => {
      await sendCookieAgreementsModal(data);
    },
    [sendCookieAgreementsModal],
  );

  return (
    <>
      <Header
        businessVariables={businessVariables}
        http={http}
        analytics={analyticsInstance}
        user={user}
      />
      <main className={styles.main}>{children}</main>
      <CookieAgreements
        businessVariables={businessVariables}
        http={http}
        config={getCookiesConfig({
          forceOpen: forceOpenCookieAgreement,
          reloadOnSave: false,
          onSubmit: () => {
            setForceOpenCookieAgreement(false);
          },
          onCookieTrackerEvent: handleCookieTrackerEvent,
        })}
      />
      <Footer
        config={getFooterConfig({
          handleAgreements: () => {
            setForceOpenCookieAgreement(true);
          },
        })}
        businessVariables={businessVariables}
        analytics={analyticsInstance}
      />
    </>
  );
};

export default HomeContent;
